
import { useToast } from "@/hooks/use-toast"
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast"
import { useIsMobile } from "@/hooks/use-mobile"
import { Check, AlertCircle, Info } from "lucide-react"
import { cn } from "@/lib/utils"
import { ToastAction, ToastActionType } from "@/hooks/use-toast"
import React from "react"

export function Toaster() {
  const { toasts } = useToast()
  const isMobile = useIsMobile()

  // Function to get the appropriate icon based on variant
  const getToastIcon = (variant?: string) => {
    switch (variant) {
      case 'success':
        return <Check className="h-5 w-5 text-green-600" />
      case 'destructive':
        return <AlertCircle className="h-5 w-5 text-red-600" />
      case 'info':
        return <Info className="h-5 w-5 text-amber-600" />
      default:
        return <Info className="h-5 w-5 text-blue-600" />
    }
  }

  // Function to render the action - handles both JSX elements and our custom action format
  const renderAction = (action: React.ReactElement | ToastAction | undefined) => {
    if (!action) return null;
    
    // If it's a React element, return it directly
    if (React.isValidElement(action)) {
      return action;
    }
    
    // Check if action is our custom type with icon
    const actionObj = action as ToastActionType;
    if (actionObj && 'icon' in actionObj) {
      const Icon = actionObj.icon;
      return (
        <div className={cn(
          "inline-flex items-center justify-center p-1.5 rounded-full", 
          actionObj.className || "bg-green-100 text-green-600"
        )}>
          <Icon className="h-5 w-5" />
        </div>
      );
    }
    
    return null;
  }

  return (
    <ToastProvider>
      {toasts.map(function ({ id, title, description, action, variant, ...props }) {
        return (
          <Toast key={id} variant={variant} {...props}>
            <div className={cn(
              "grid gap-1", 
              isMobile && "w-full"
            )}>
              {title && (
                <ToastTitle>
                  {getToastIcon(variant as string)}
                  {title}
                </ToastTitle>
              )}
              {description && (
                <ToastDescription>{description}</ToastDescription>
              )}
            </div>
            {renderAction(action)}
            <ToastClose />
          </Toast>
        )
      })}
      <ToastViewport />
    </ToastProvider>
  )
}
